import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Plus, ChevronRight, Globe, Target, Calendar } from 'lucide-react';

function ScoreBadge({ score }) {
  if (score === null || score === undefined) return <span className="text-sm text-[#64748B]">No audit yet</span>;
  const color = score > 80 ? '#22C55E' : score > 50 ? '#DDA73D' : '#EF4444';
  return (
    <div className="flex items-center gap-2">
      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-heading font-semibold text-sm" style={{ backgroundColor: color }}>
        {score}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 data-testid="dashboard-heading" className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F2951]">Your Projects</h1>
            <p className="text-base text-[#64748B] mt-1">Manage and audit your local business SEO</p>
          </div>
          <Button
            data-testid="new-project-button"
            onClick={() => navigate('/projects/new')}
            className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-6 h-11 font-medium transition-all duration-200 hover:-translate-y-[1px]"
          >
            <Plus className="w-4 h-4 mr-2" /> New Project
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-[#E2E8F0] p-6 animate-pulse">
                <div className="h-5 bg-[#EFF3F8] rounded w-2/3 mb-3" />
                <div className="h-4 bg-[#EFF3F8] rounded w-1/2 mb-6" />
                <div className="h-10 bg-[#EFF3F8] rounded-full w-10" />
              </div>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-[#EFF3F8] rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Globe className="w-10 h-10 text-[#0268C4]" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-xl font-medium text-[#0F2951] mb-2">No projects yet</h2>
            <p className="text-[#64748B] mb-6 max-w-sm mx-auto">Create your first project to start auditing your local SEO and climbing the rankings.</p>
            <Button data-testid="empty-new-project-button" onClick={() => navigate('/projects/new')} className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-6 h-11">
              <Plus className="w-4 h-4 mr-2" /> Create First Project
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map(project => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                data-testid={`project-card-${project.id}`}
                className="group bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)] hover:shadow-[0_8px_32px_rgba(44,48,46,0.08)] transition-all duration-200 hover:-translate-y-[1px]"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg font-medium text-[#0F2951] truncate">{project.business_name}</h3>
                    <p className="text-sm text-[#64748B] mt-0.5">{project.business_type} &middot; {project.city}{project.state ? `, ${project.state}` : ''}</p>
                  </div>
                  <ScoreBadge score={project.latest_score} />
                </div>

                <p className="text-sm text-[#64748B] line-clamp-2 mb-4 italic">"{project.intention}"</p>

                <div className="flex items-center justify-between pt-4 border-t border-[#E2E8F0]">
                  <div className="flex items-center gap-3 text-xs text-[#64748B]">
                    <span className="flex items-center gap-1"><Target className="w-3.5 h-3.5" />{project.audit_count} audits</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{project.time_horizon}d</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#64748B] group-hover:text-[#0268C4] transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
